import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { fetchShopProduct, fetchShopProducts, absoluteShopUrl } from "../../../shop/api";
import { useCart } from "../../../shop/CartContext";

export function SingleProductCTA({ shopSlug, fallbackCategory }) {
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(!shopSlug);
  const cart = useCart();

  useEffect(() => {
    if (!shopSlug) return;
    fetchShopProduct(shopSlug)
      .then(setProduct)
      .catch(() => setNotFound(true));
  }, [shopSlug]);

  if (notFound) {
    return (
      <section className="border-t border-line/70" data-testid="shop-cta-browse">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-10 py-16 lg:py-20 text-center">
          <p className="eyebrow mb-4">Shop GenieX</p>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight max-w-[32ch] mx-auto">
            Ready to bring this home? Explore our {fallbackCategory || "Smart Home"} collection.
          </h2>
          <Link
            to={`/shop${fallbackCategory ? `?category=${encodeURIComponent(fallbackCategory)}` : ""}`}
            data-testid="shop-cta-browse-link"
            className="btn-gold mt-8 inline-flex"
          >
            Browse the Shop <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    );
  }

  if (!product) return null;

  return (
    <section className="border-t border-line/70 bg-[var(--gx-black)]" data-testid="shop-cta-single">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <p className="eyebrow mb-8 text-center">Shop This Product</p>
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 lg:gap-14 items-center card-lux p-6 lg:p-10">
          <Link to={`/shop/${product.slug}`} data-testid="shop-cta-image-link" className="block aspect-square rounded-2xl overflow-hidden bg-[var(--gx-surface-2)]">
            <img src={absoluteShopUrl(product.images?.[0])} alt={product.name} className="w-full h-full object-contain p-6" />
          </Link>
          <div>
            <Link to={`/shop/${product.slug}`} className="font-display text-2xl sm:text-3xl tracking-tight hover:text-gold transition-colors">
              {product.name}
            </Link>
            <p className="text-dim text-[14px] mt-3 leading-relaxed max-w-[50ch]">{product.description}</p>
            <div className="flex items-center gap-3 mt-5">
              <span className="text-gold font-display text-2xl">₹{Number(product.price).toLocaleString("en-IN")}</span>
              {product.compare_at_price ? (
                <span className="text-mute text-sm line-through">₹{Number(product.compare_at_price).toLocaleString("en-IN")}</span>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-3 mt-7">
              <button
                type="button"
                disabled={product.stock <= 0}
                onClick={() => {
                  cart.addItem(product, 1);
                  toast.success(`${product.name} added to cart`);
                }}
                data-testid="shop-cta-add-to-cart"
                className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {product.stock <= 0 ? "Out of Stock" : (
                  <>Add to Cart <ShoppingBag className="w-4 h-4" /></>
                )}
              </button>
              <Link to={`/shop/${product.slug}`} data-testid="shop-cta-view-product" className="btn-outline">
                View Details <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function RecommendedProductsStrip({ categories = [] }) {
  const [products, setProducts] = useState([]);
  const cart = useCart();
  const catKey = categories.join(",");

  useEffect(() => {
    fetchShopProducts()
      .then((d) => {
        const all = d.products || [];
        const matched = all.filter((p) => categories.includes(p.category));
        const rest = all.filter((p) => !categories.includes(p.category));
        setProducts([...matched, ...rest].slice(0, 4));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catKey]);

  if (!products.length) return null;

  return (
    <section className="border-t border-line/70" data-testid="recommended-products-section">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-24">
        <p className="eyebrow mb-4">Recommended Products</p>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight max-w-[36ch]">Everything you need to bring this to life.</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {products.map((p) => (
            <div key={p.id} data-testid={`recommended-product-${p.slug}`} className="card-lux overflow-hidden group">
              <Link to={`/shop/${p.slug}`} className="block aspect-square overflow-hidden bg-[var(--gx-surface-2)]">
                <img src={absoluteShopUrl(p.images?.[0])} alt={p.name} className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500" />
              </Link>
              <div className="p-4">
                <Link to={`/shop/${p.slug}`} className="font-display text-[15px] block hover:text-gold transition-colors line-clamp-1">
                  {p.name}
                </Link>
                <span className="text-gold font-display text-lg mt-2 block">₹{Number(p.price).toLocaleString("en-IN")}</span>
                <button
                  type="button"
                  disabled={p.stock <= 0}
                  onClick={() => {
                    cart.addItem(p, 1);
                    toast.success(`${p.name} added to cart`);
                  }}
                  data-testid={`recommended-add-to-cart-${p.id}`}
                  className="btn-outline w-full mt-4 text-[11px] py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {p.stock <= 0 ? "Out of Stock" : "Add to Cart"}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/shop" data-testid="recommended-view-all-shop" className="btn-outline inline-flex">
            View Full Shop <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
